import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';
import { getBulkPresence } from '../../shared/redis';

export async function getOrCreateDM(userIdA: string, userIdB: string) {
  const isSelfChat = userIdA === userIdB;

  if (isSelfChat) {
    // Self-chat: find a conversation where the only member is this user
    const existing = await prisma.conversation.findFirst({
      where: {
        type: 'direct',
        members: {
          some: { userId: userIdA },
          every: { userId: userIdA }
        }
      },
      include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
    });
    // Only return if it's truly a single-member conversation
    if (existing && existing.members.length === 1) return existing;


    return prisma.conversation.create({
      data: {
        type: 'direct',
        createdBy: userIdA,
        members: { create: [{ userId: userIdA }] },
      },
      include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
    });
  }

  // Check if DM already exists with exactly these two members
  const existing = await prisma.conversation.findFirst({
    where: {
      type: 'direct',
      AND: [
        { members: { some: { userId: userIdA } } },
        { members: { some: { userId: userIdB } } }
      ],
      // Ensure there are exactly 2 members
      members: {
        every: { userId: { in: [userIdA, userIdB] } }
      }
    },
    include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
  });
  if (existing && existing.members.length === 2) return existing;

  return prisma.conversation.create({
    data: {
      type: 'direct',
      createdBy: userIdA,
      members: { create: [{ userId: userIdA }, { userId: userIdB }] },
    },
    include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
  });
}

export async function createGroupConversation(creatorId: string, memberIds: string[], name?: string) {
  const allMembers = Array.from(new Set([creatorId, ...memberIds]));
  return prisma.conversation.create({
    data: {
      type: 'group',
      name,
      createdBy: creatorId,
      members: { create: allMembers.map((userId) => ({ userId })) },
    },
    include: { members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, email: true, company: true, department: true } } } } },
  });
}

export async function listConversations(userId: string, limit: number = 50, cursor?: string) {
  const userRoles = await prisma.userRole.findMany({
    where: { userId },
    include: { role: true },
  });
  const isAdmin = userRoles.some(ur => ur.role.name === 'admin' || ur.role.name === 'super_admin');

  const where: any = { members: { some: { userId } } };

  const conversations = await prisma.conversation.findMany({
    where,
    take: limit,
    ...(cursor ? { skip: 1, cursor: { id: cursor } } : {}),
    include: {
      members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, presence: true, email: true, company: true, department: true } } } },
      messages: {
        where: { isDeleted: false },
        orderBy: { createdAt: 'desc' },
        take: 1,
        select: {
          id: true,
          content: true,
          createdAt: true,
          senderId: true,
          metadata: true,
          sender: { select: { id: true, displayName: true } },
        },
      },
    },
    orderBy: { createdAt: 'desc' },
  });

  const allUserIds = Array.from(new Set(conversations.flatMap(c => c.members.map(m => m.userId))));
  const presenceMap = await getBulkPresence(allUserIds);

  const conversationsWithUnread = await Promise.all(
    conversations.map(async (conv) => {
      conv.members.forEach(m => {
        if (m.user) {
          m.user.presence = (presenceMap[m.userId] || 'offline') as any;
        }
      });

      const myMembership = conv.members.find((m) => m.userId === userId);
      const lastReadAt = myMembership?.lastReadAt ?? new Date(0);

      // For self-chat, count all messages (including own) as potential unread
      // Self-chat is detected by all members having the same userId (single member)
      const isSelfChat = conv.members.every(m => m.userId === userId) && conv.members.length === 1;

      const unreadCount = await prisma.message.count({
        where: {
          conversationId: conv.id,
          isDeleted: false,
          ...(isSelfChat ? {} : { senderId: { not: userId } }),
          createdAt: { gt: lastReadAt },
        },
      });

      return { ...conv, unreadCount, myLastReadAt: lastReadAt, isSelfChat };
    })
  );

  return conversationsWithUnread;
}
